import { Button, Typography } from "antd";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { currentSiteDomain } from "../core/site-domain";
import "./landing-page.css";

const SITE_DOMAIN = currentSiteDomain();

const NAMES = [
  {
    title: "ИТ Окна",
    text: "Десктопная система для Windows. Считаете заказы и конструкции на компьютере в цехе или офисе."
  },
  {
    title: "ВЕБ Окна",
    text: "Онлайн-система в браузере. Та же логика работы — на любом компьютере, планшете или телефоне, без установки."
  }
];

const BENEFITS = [
  {
    title: "Свой сайт и своя база",
    text: "Копия открывается как company.webokna.online. Данные компании не смешиваются с чужими."
  },
  {
    title: "От расчёта до цеха",
    text: "Заказы, конструкции, пакеты, раскрой, клиенты и отчёты — один контур вместо таблиц и разрозненных программ."
  },
  {
    title: "Без установки",
    text: "Работа из браузера на любом компьютере, планшете или телефоне. Копию поднимает кабинет на сервере: вы выбираете имя и запускаете приложение."
  },
  {
    title: "Мессенджеры с клиентами",
    text: "Прямые коммуникации с клиентами через мессенджеры — переписка в том же контуре, что заказы и производство."
  },
  {
    title: "Нейросетевые агенты",
    text: "Агенты для общения с клиентами: отвечают в каналах, пока менеджер занят заказом или в цехе."
  },
  {
    title: "Кабинет рядом с программой",
    text: "После запуска в кабинете остаются пользователи базы, настройки, онлайн-отчёты и резервные копии."
  }
];

const MIGRATION = [
  {
    title: "Своя база с десктопа",
    text: "Загрузите копию базы из десктопной ИТ Окна — заказы, клиенты и справочники останутся на месте."
  },
  {
    title: "Или только настройки расчёта",
    text: "Файл обмена с десктопа: цены, формулы, профили. Чистая база и ваш расчёт — быстрый старт."
  },
  {
    title: "Все возможности на месте",
    text: "Тот же контур работы, включая обмен между компаниями и отделами через облако."
  },
  {
    title: "Постепенный переход",
    text: "ИТ Окна и ВЕБ Окна можно вести одновременно: часть сотрудников остаётся на десктопе, часть уже в браузере."
  }
];

const STEPS = [
  {
    title: "Создайте кабинет",
    text: "Укажите имя, компанию и почту. На почту придёт код — пароль не нужен."
  },
  {
    title: "Выберите имя сайта",
    text: "Короткое латинское имя станет адресом вашей копии: имя.webokna.online."
  },
  {
    title: "Запустите приложение",
    text: "Кабинет создаст базу из эталона и поднимет программу на вашем адресе."
  },
  {
    title: "Загрузите базу или настройки",
    text: "С десктопной ИТ Окна можно перенести полную базу или только расчёт. Либо сразу работать на эталоне."
  },
  {
    title: "Откройте программу",
    text: "Вход в ВЕБ Окна отдельный. Заведите клиентов и первый заказ — можно работать."
  }
];

const LandingPage = () => {
  const navigate = useNavigate();
  const session = useSelector((state) => state.user.session);

  const renderActions = () =>
    session ? (
      <Button type="primary" size="large" onClick={() => navigate("/cabinet")}>
        Открыть кабинет
      </Button>
    ) : (
      <>
        <Button type="primary" size="large" onClick={() => navigate("/auth?register=1")}>
          Создать кабинет
        </Button>
        <Button size="large" onClick={() => navigate("/auth")}>
          Войти
        </Button>
      </>
    );

  return (
    <div className="landing">
      <section className="landing-hero">
        <img className="landing-logo" src="/favicon.svg" alt="" width={56} height={56} />
        <div className="landing-kicker">{SITE_DOMAIN}</div>
        <Typography.Title className="landing-title">Программа для оконного производства</Typography.Title>
        <Typography.Paragraph className="landing-lead">
          ВЕБ Окна — онлайн-система для оконного производства: конструкции, заказы, цех. ИТ Окна —
          десктопная программа для Windows. Это две стороны одной системы: можно перейти сразу или
          работать параллельно, загрузив базу или настройки с десктопа.
        </Typography.Paragraph>
        <div className="landing-actions">
          {renderActions()}
          <Button size="large" onClick={() => navigate("/start")}>
            Как начать работу
          </Button>
        </div>
        <div className="landing-features landing-names">
          {NAMES.map((item) => (
            <article key={item.title} className="landing-feature">
              <h3>{item.title}</h3>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {item.text}
              </Typography.Paragraph>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <Typography.Title level={3} className="landing-section-title">
          Назначение
        </Typography.Title>
        <Typography.Paragraph className="landing-prose">
          Система для компаний, которые проектируют и выпускают окна. Менеджер оформляет заказ и рисует изделия,
          производство собирает пакеты и режет профили, руководство смотрит суммы и отчёты. У каждой компании
          своя копия и своя база: справочники, цены и история заказов остаются у вас.
        </Typography.Paragraph>
      </section>

      <section className="landing-section">
        <Typography.Title level={3} className="landing-section-title">
          Преимущества
        </Typography.Title>
        <Typography.Paragraph className="landing-prose">
          По сравнению с десктопной ИТ Окна в ВЕБ Окна можно работать из браузера на любом устройстве,
          переписываться с клиентами в мессенджерах и подключать нейросетевых агентов.
        </Typography.Paragraph>
        <div className="landing-features">
          {BENEFITS.map((item) => (
            <article key={item.title} className="landing-feature">
              <h3>{item.title}</h3>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {item.text}
              </Typography.Paragraph>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <Typography.Title level={3} className="landing-section-title">
          Переход с ИТ Окна
        </Typography.Title>
        <Typography.Paragraph className="landing-prose">
          ВЕБ Окна — онлайн-версия той же системы, что и десктопная ИТ Окна. Не нужно заново настраивать
          расчёт и терять обмен между компаниями и отделами: загрузите базу с Windows или файл настроек.
          Переход может быть постепенным — ИТ Окна и ВЕБ Окна работают одновременно, пока вся компания
          не перейдёт в браузер.
        </Typography.Paragraph>
        <div className="landing-features">
          {MIGRATION.map((item) => (
            <article key={item.title} className="landing-feature">
              <h3>{item.title}</h3>
              <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                {item.text}
              </Typography.Paragraph>
            </article>
          ))}
        </div>
      </section>

      <section className="landing-section">
        <Typography.Title level={3} className="landing-section-title">
          Как начать
        </Typography.Title>
        <ol className="landing-steps">
          {STEPS.map((item, index) => (
            <li key={item.title} className="landing-step">
              <span className="landing-step-num">{index + 1}</span>
              <div>
                <h3>{item.title}</h3>
                <Typography.Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {item.text}
                </Typography.Paragraph>
              </div>
            </li>
          ))}
        </ol>
        <div className="landing-actions landing-actions-end">
          {renderActions()}
          <Button size="large" onClick={() => navigate("/start")}>
            Инструкция со скриншотами
          </Button>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
